import IntercomSdk, {
  boot,
  shutdown,
  update,
} from '@intercom/messenger-js-sdk';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { FCC } from '@eluve/components';
import { Logger, useNamedLogger } from '@eluve/logger';
import { FeatureFlagKeysLookup } from '@eluve/posthog';
import { useSession, useUserIdFromSession } from '@eluve/session-helpers';
import { formatHumanName } from '@eluve/utils';

import { appConfig, isProdIntercomEnv } from './config';

const INTERCOM_DEFAULT_WHEN_POSTHOG_FAILS = false;

type IntercomStatus = {
  isIntercomReady: boolean;
};

const IntercomContext = createContext<IntercomStatus>({
  isIntercomReady: false,
});

export const useIntercomStatus = () => useContext(IntercomContext);

type IntercomUserData = {
  userId: string;
  email: string;
  name?: string;
  createdAt?: number;
};

// ---------------------------------------------------------------------------
// Module-level Intercom session controller.
//
// `window.Intercom` is a singleton that survives React unmount/remount, so a
// single logical Intercom session must span the anonymous (`/login`) →
// identified (app) transition. We therefore track boot state here, NOT per
// hook instance: the `/login` `AnonymousIntercomProvider` and the
// authenticated `IntercomProvider` are separate component instances that must
// coordinate on the same session.
//
// The nameless-contact bug (ENG-4943): the old code called `shutdown()` when
// the `/login` route unmounted, severing the anonymous session from the
// identified boot that followed. Intercom stamped our `external_id` onto the
// orphaned lead but dropped its email/name → `role:user` with empty email.
// The fix: never `shutdown()` across the login boundary. Boot once, then
// `update()` with identity on login to convert the lead in-session, and only
// `shutdown()` on an explicit teardown (logout, flag off, or non-prod env).
// ---------------------------------------------------------------------------

let sdkInitialized = false;
let isBooted = false;
// The user_id of the currently-booted identified session, or undefined when
// the live session is anonymous. Lets the controller decide boot vs. in-session
// `update()` vs. reset without re-reading Intercom's opaque cookie state.
let bootedUserId: string | undefined;

const ensureIntercomSdkInitialized = () => {
  if (!sdkInitialized) {
    IntercomSdk({ app_id: appConfig.VITE_INTERCOM_APP_ID });
    sdkInitialized = true;
  }
};

const identityArgs = (userData: IntercomUserData) => ({
  user_id: userData.userId,
  email: userData.email,
  created_at: userData.createdAt,
  ...(userData.name && { name: userData.name }),
});

const buildBootConfig = (userData?: IntercomUserData) => {
  const baseConfig = {
    app_id: appConfig.VITE_INTERCOM_APP_ID,
    hide_default_launcher: true,
  };
  if (!userData) {
    return baseConfig;
  }
  return { ...baseConfig, ...identityArgs(userData) };
};

const bootIntercom = (userData: IntercomUserData | undefined) => {
  boot(buildBootConfig(userData));
  isBooted = true;
  bootedUserId = userData?.userId;
};

/**
 * Brings the shared Intercom session to the desired state while minimizing
 * teardown. Returns false if the SDK failed to initialize.
 *
 * - Not booted → boot (anonymous or identified).
 * - Anonymous, now identified → `update()` to convert the lead in the SAME
 *   session. This is the ENG-4943 fix: no `shutdown()` across the login
 *   boundary, so the converted contact keeps its email/name.
 * - Identified, now anonymous (logout returned the user to `/login`) or a
 *   different user → `shutdown()` then re-boot, clearing the previous user.
 *   This teardown is at the app→login (logout) boundary, never login→app.
 */
const activateIntercom = (
  userData: IntercomUserData | undefined,
  logger: Logger,
): boolean => {
  ensureIntercomSdkInitialized();

  // If window.Intercom is undefined after SDK init, the IntercomSdk() call
  // was likely removed or broken — Intercom will not load for users.
  if (!window.Intercom) {
    logger.error('Intercom failed to initialize');
    return false;
  }

  const targetUserId = userData?.userId;

  if (!isBooted) {
    bootIntercom(userData);
    return true;
  }

  if (bootedUserId === targetUserId) {
    // Same identity already live — refresh attributes if identified, else no-op.
    if (userData) {
      update(identityArgs(userData));
    }
    return true;
  }

  if (!bootedUserId && userData) {
    // Anonymous lead → identified user, same session (no shutdown).
    update(identityArgs(userData));
    bootedUserId = targetUserId;
    return true;
  }

  // Identity changed away from the booted user (logout → anonymous, or a user
  // switch): reset the session cleanly.
  shutdown();
  bootIntercom(userData);
  return true;
};

/**
 * Tears the shared Intercom session down. Call on explicit teardown only —
 * feature flag turning off or a non-prod environment — never on a route
 * unmount that crosses the login boundary.
 */
export const shutdownIntercom = (logger?: Logger, reason?: string) => {
  if (!isBooted) {
    return;
  }
  try {
    shutdown();
    isBooted = false;
    bootedUserId = undefined;
    logger?.info(`Intercom shut down${reason ? ` ${reason}` : ''}`);
  } catch (error) {
    logger?.error(`Failed to shut down Intercom${reason ? ` ${reason}` : ''}`, {
      error,
    });
  }
};

/**
 * Test-only: fully resets the module-level controller (including
 * `sdkInitialized`, which `shutdownIntercom` deliberately leaves alone) so each
 * test starts from a clean, order-independent state.
 */
export const resetIntercomForTests = () => {
  sdkInitialized = false;
  isBooted = false;
  bootedUserId = undefined;
};

type UseIntercomOptions = {
  /** Whether the caller is ready for Intercom to boot. Defaults to true. */
  isEnabled?: boolean;
  /** Logger name for debugging. */
  loggerName: string;
  /** User data for authenticated mode. If undefined, boots in anonymous mode. */
  userData?: IntercomUserData;
};

/**
 * Manages the Intercom lifecycle for a single provider:
 * - Boots (or converts via `update()`) when the feature flag, prod-env gate,
 *   and `isEnabled` are all true.
 * - Shuts the shared session down when the flag or `isEnabled` turns off.
 * - Does NOT shut down on unmount — the session is owned by the module-level
 *   controller and must survive the `/login` → app transition. After logout,
 *   returning to `/login` re-mounts `AnonymousIntercomProvider`, whose activate
 *   call resets the stale identified session (see `activateIntercom`).
 */
const useIntercom = (options: UseIntercomOptions): boolean => {
  const { userData, isEnabled = true, loggerName } = options;
  const logger = useNamedLogger(loggerName);
  const [isReady, setIsReady] = useState(false);

  const isFeatureFlagEnabled =
    useFeatureFlagEnabled(FeatureFlagKeysLookup.INTERCOM_INTEGRATION) ??
    INTERCOM_DEFAULT_WHEN_POSTHOG_FAILS;

  // A global kill (flag off / non-prod) tears the shared session down. A
  // provider merely not being ready (`isEnabled === false`) must NOT — see the
  // effect below.
  const isIntercomGloballyEnabled = isProdIntercomEnv && isFeatureFlagEnabled;

  // Serialize just the identity so the effect re-runs when it changes (e.g.
  // anonymous → identified on login) without depending on object identity.
  const userDataKey = userData
    ? `${userData.userId}|${userData.email}|${userData.name ?? ''}|${userData.createdAt ?? ''}`
    : '';

  useEffect(() => {
    let pollId: ReturnType<typeof setInterval> | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const clearTimers = () => {
      if (pollId) clearInterval(pollId);
      if (timeoutId) clearTimeout(timeoutId);
    };

    if (!isIntercomGloballyEnabled) {
      setIsReady(false);
      shutdownIntercom(
        logger,
        `due to ${!isProdIntercomEnv ? 'non-prod environment' : 'feature flag being disabled'}`,
      );
      return;
    }

    // This provider isn't ready yet (e.g. session data still resolving on the
    // login→app transition). Stay inactive but do NOT shut down: tearing the
    // shared session down here would sever the anonymous session before it can
    // be converted. A later render (once `isEnabled` flips true) reaches
    // `activateIntercom`, which then `update()`s the live session instead of
    // re-booting it.
    if (!isEnabled) {
      setIsReady(false);
      return;
    }

    try {
      const activated = activateIntercom(userData, logger);
      if (!activated) {
        return;
      }

      // The Intercom SDK stub queues calls in window.Intercom.q until the
      // real widget script loads from the CDN and replaces the stub.
      // If the script is blocked (ad blocker, network), .q persists and
      // the messenger will never work — so we only mark ready once .q is gone.
      const isWidgetLoaded = () =>
        Boolean(window.Intercom && !('q' in window.Intercom));

      const onReady = () => {
        setIsReady(true);
        logger.info('Intercom active', {
          mode: userData ? 'authenticated' : 'anonymous',
        });
      };

      if (isWidgetLoaded()) {
        onReady();
      } else {
        pollId = setInterval(() => {
          if (isWidgetLoaded()) {
            clearTimers();
            onReady();
          }
        }, 250);

        timeoutId = setTimeout(() => {
          clearTimers();
          logger.warn('Intercom widget failed to load');
        }, 5000);
      }
    } catch (error) {
      logger.error('Failed to boot Intercom', { error });
    }

    // NOTE: no shutdown() here. Unmounting a provider (e.g. leaving `/login`
    // on login) must NOT tear the session down — that is the ENG-4943 bug.
    return () => {
      clearTimers();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isIntercomGloballyEnabled, isEnabled, userDataKey, logger]);

  return isReady;
};

/**
 * Boots Intercom in anonymous visitor mode for logged-out pages (e.g.
 * `/login`), enabling login-screen messaging/banners. Does not read session
 * data, so it is safe to mount outside the authenticated tree.
 */
export const AnonymousIntercomProvider: FCC = ({ children }) => {
  useIntercom({ loggerName: 'AnonymousIntercomProvider' });
  return children;
};

/**
 * Boots (or converts an existing anonymous session to) an identified Intercom
 * user for the authenticated app. Waits for session data before attaching
 * identity so the lead converts cleanly with email/name.
 */
export const IntercomProvider: FCC = ({ children }) => {
  const userId = useUserIdFromSession();
  const session = useSession();

  const hasSessionData = Boolean(userId && session?.email);

  const userData = useMemo(() => {
    if (!userId || !session?.email) {
      return undefined;
    }
    const fullName = formatHumanName(session.firstName, session.lastName);
    return {
      userId,
      email: session.email,
      name: fullName || undefined,
      createdAt: session.createdAt,
    };
  }, [
    userId,
    session?.email,
    session?.firstName,
    session?.lastName,
    session?.createdAt,
  ]);

  const isIntercomReady = useIntercom({
    isEnabled: hasSessionData,
    loggerName: 'IntercomProvider',
    userData,
  });

  const contextValue = useMemo(() => ({ isIntercomReady }), [isIntercomReady]);

  return (
    <IntercomContext.Provider value={contextValue}>
      {children}
    </IntercomContext.Provider>
  );
};

/**
 * Programmatically opens the Intercom messenger panel.
 * Safe to call even if Intercom hasn't loaded — silently no-ops.
 */
export const showIntercom = () => {
  if (window.Intercom) {
    window.Intercom('show');
  }
};
